import { configuration, updateConfiguration } from './configuration'
import { icons } from './icons'
import {
  clearTablesListener,
  isDevelopment,
  menuItemsListener,
  reactElementSelectorListener,
  searchListener,
  serverCheckerListener,
  toggleDevToolsListener,
  toggleElementSelectorListener,
  toggleSizeListener,
} from './initHelpers'

const init = (_initConfiguration: typeof configuration) => {
  const existingDevTools = document.querySelector('#fu-tools')

  if (existingDevTools) {
    return
  }

  updateConfiguration(_initConfiguration)

  document.body.insertAdjacentHTML(
    'beforeend',
    `<div id="fu-tools" class="select-none fixed bottom-3 left-1/2 transform -translate-x-1/2 pointer-events-none z-999999">
      <div class="bg-[#0f172a]/95 rounded-full inline-flex items-center pointer-events-auto cursor-pointer group hover:bg-[#020617] transition-all duration-200 relative ring-1 ring-white/5 shadow-lg shadow-black/40 backdrop-blur">
        <div class="bg-[#1e293b] rounded-full w-7 h-7 flex items-center justify-center ml-1.5 my-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-[#94a3b8] group-hover:text-[#22d3ee] duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">${icons.devtools}</svg>
        </div>
        <span class="text-white text-[11px]/[18px] font-mono font-medium tracking-[0.18em] px-2.5">DEVTOOLS</span>
        <div id="fu-tools-element-selector" data-fu-tools-element-selector="inactive" class="h-10 flex items-center ${isDevelopment() ? '' : 'fu-tools-element-selector-not-available'} [&.fu-tools-element-selector-not-available]:pr-2 [&:not(.fu-tools-element-selector-not-available)]:pr-3 py-2 data-[fu-tools-element-selector=active]:text-[#22d3ee] data-[fu-tools-element-selector=inactive]:text-[#94a3b8] flex group-hover:text-[#22d3ee]! group/rotate">
          <svg class="[.fu-tools-element-selector-not-available_&]:hidden duration-200 group-hover/rotate:rotate-360 transition-all" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">${icons.selectors}</svg>
        </div>
        <div data-fu-tools-badge-raw="" data-fu-tools-badge="" class="data-[fu-tools-badge]:[&:not([data-fu-tools-badge=''])]:after:bg-[#ef4444] data-[fu-tools-badge]:[&:not([data-fu-tools-badge=''])]:after:ring-2 data-[fu-tools-badge]:[&:not([data-fu-tools-badge=''])]:after:ring-[#0f172a] after:transition after:duration-200 after:h-5 after:w-5 after:-top-1.5 after:-right-1.5 after:text-center after:rounded-full after:content-[attr(data-fu-tools-badge)] after:text-white after:text-[11px]/[20px] after:font-semibold after:absolute"></div>
      </div>
    </div>

    <div id="fu-tools-content" data-fu-tools-content="closed" data-fu-tools-size="default" class="flex-col hidden data-[fu-tools-content=open]:flex overflow-hidden font-mono w-full h-full data-[fu-tools-size=small]:max-h-[450px] data-[fu-tools-size=small]:max-w-7/10 data-[fu-tools-size=default]:max-h-[550px] data-[fu-tools-size=default]:max-w-9/10 data-[fu-tools-size=large]:max-h-9/10 data-[fu-tools-size=large]:max-w-[calc(100%-50px)] transition-all duration-200 bg-[#0f172a] rounded-2xl ring-1 ring-white/5 shadow-2xl shadow-black/50 fixed bottom-16 left-1/2 transform -translate-x-1/2 z-999999">
      <div class="bg-[#1e293b]/80 backdrop-blur px-5 py-3.5 gap-3 flex-row flex items-center justify-between border-b border-white/5">
        <div class="flex items-center gap-2.5">
          <span class="text-white font-semibold tracking-tight">FaceUp</span>
          <a target="_blank" ${isDevelopment() ? '' : `href="https://faceup.sentry.io/issues/?project=5425818&query=url%3A${encodeURIComponent(configuration.apiUrl)}&sort=date&statsPeriod=30d"`} class="[&:not([href])]:cursor-auto hidden min-[350px]:inline bg-[#334155]/60! text-[#cbd5e1]! [&[href]]:hover:text-white! [&[href]]:hover:bg-[#334155]! text-[11px] font-medium tracking-wide rounded-full px-2.5 py-0.5 transition duration-200">DevTools</a>
        </div>
        <div class="flex flex-row gap-2 items-center">
          <div title="Open GraphQL Playground" id="fu-tools-server-checker" class="hidden md:flex items-center justify-between bg-[#334155]/60 text-[#cbd5e1] text-[11px] font-medium gap-2 rounded-full pl-2.5 pr-2 py-1.5 group hover:text-white hover:bg-[#334155] cursor-pointer transition duration-200">
            <div data-fu-tools-server-status class="w-1.5 h-1.5 data-[fu-tools-server-status]:bg-[#94a3b8] data-[fu-tools-server-status=online]:bg-[#10b981] data-[fu-tools-server-status=online]:shadow-[0_0_8px_rgba(16,185,129,0.6)] data-[fu-tools-server-status=offline]:bg-[#ef4444] rounded-full transition-all"></div>
            <span>${configuration.apiUrl}</span>
            <a title="Open in new tab" href="${configuration.apiUrl}" target="_blank" class="text-[#cbd5e1]! hover:text-white! duration-200 transition-all flex">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">${icons.server}</svg>
            </a>
          </div>
          <div id="fu-tools-size" class="text-[#94a3b8] flex hover:text-[#22d3ee] cursor-pointer transition duration-200 rounded-md p-1 hover:bg-white/5" title="Toggle maximization">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">${icons.toggleSize}</svg>
          </div>
        </div>
      </div>

      <div data-fu-tools-menu-active="${configuration.plugins[0]?.id}" class="flex bg-[#0f172a] flex-1 overflow-hidden relative">
        <div class="text-center py-2 border-white/5 border-r">
          <div class="p-1.5 gap-0.5 flex flex-col text-[#94a3b8]">
            ${configuration.plugins
              .map(
                ({ icon, name, id }) => `
                <div data-fu-tools-menu="${id}" title="${name}" class="flex items-center justify-center transition-all hover:bg-white/5 hover:text-[#22d3ee] rounded-lg cursor-pointer p-2 relative">
                  <div data-fu-tools-menu-badge-raw="" data-fu-tools-menu-badge="" class="data-[fu-tools-menu-badge]:[&:not([data-fu-tools-menu-badge=''])]:after:bg-[#ef4444] after:transition after:duration-200 after:h-4 after:w-4 after:-top-0.5 after:-right-0.5 after:text-center after:rounded-full after:content-[attr(data-fu-tools-menu-badge)] after:text-white after:text-[10px]/[16px] after:font-semibold after:absolute"></div>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">${icon}</svg>
                </div>`
              )
              .join('')}
          </div>
        </div>

        <div class="flex-1 overflow-auto">
        ${configuration.plugins
          .map(
            ({ id, name, layout, extra }) => `
          <div class="h-full" data-fu-tools-content="${id}">
            ${layout.type === 'full' ? layout.render() : ''}
            ${
              layout.type === 'table'
                ? `
            <div class="pt-5 px-5 flex items-center justify-between mb-3 text-white gap-2">
              <span class="font-semibold tracking-tight block">${name}</span>
              <div class="gap-2 flex flex-row">
                ${extra}
                <div class="cursor-pointer flex rounded-md p-1 -m-1 hover:bg-white/5" data-fu-tools-clear-table="${id}" title="Clear">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-[#94a3b8] hover:text-[#22d3ee] duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">${icons.clear}</svg>
                </div>
              </div>
            </div>
            <div class="pb-4 px-5 min-w-full divide-y divide-white/5">
              ${layout.searchColumnsIndex ? `<input type="text" data-fu-tools-search-input="${id}" data-fu-tools-search-input-columns="${layout.searchColumnsIndex}" class="w-full bg-[#1e293b] px-3 py-2 my-2 text-[12px]/[16px] text-[#cbd5e1] placeholder-[#94a3b8] rounded-lg ring-1 ring-white/5 border-0 focus:ring-2 focus:ring-[#22d3ee] focus:outline-none transition" placeholder="Search..." />` : ''}
              <div class="flex sticky top-0 bg-[#0f172a]">
              ${(layout.type === 'table' ? layout.columns : [])
                .map(item =>
                  Array.isArray(item)
                    ? item
                        .map(
                          i =>
                            `<div class="px-4 py-2 text-left text-[10px] font-semibold tracking-[0.08em] text-[#94a3b8] uppercase w-full">${i}</div>`
                        )
                        .join('')
                    : `<div class="px-4 py-2 text-left text-[10px] font-semibold tracking-[0.08em] text-[#94a3b8] uppercase w-full ${item.class}">${item.name}</div>`
                )
                .join('')}
              </div>
              <div data-fu-tools-table-content="${id}" class="divide-y divide-white/5"></div>
            </div>`
                : ''
            }
          </div>`
          )
          .join('')}
        </div>
      </div>
    </div>

    <dialog id="fu-tools-dialog" class="top-0 left-0 m-0 p-0 font-mono z-9999999">
      <div class="fixed inset-0 bg-[#020617]/70 backdrop-blur-sm"></div>
      <div class="fixed inset-0 flex items-center justify-center p-4">
        <div class="text-white bg-[#0f172a] rounded-2xl ring-1 ring-white/10 shadow-2xl shadow-black/50 p-5 max-w-9/10 w-full max-h-9/10 flex flex-col">
          <div class="outline-0 flex-1 overflow-auto mt-1 break-words whitespace-break-spaces text-[13px]/[1.55]" id="fu-tools-dialog-content"></div>
          <form method="dialog" class="flex justify-end mt-4">
            <button class="bg-[#1e293b] hover:bg-[#334155] ring-1 ring-white/5 transition duration-200 font-medium py-2 px-4 rounded-lg border-0 cursor-pointer text-white!" type="submit">Close</button>
          </form>
        </div>
      </div>
    </dialog>

    <style>
      ${configuration.plugins
        .map(
          ({ id }) => `
        [data-fu-tools-menu-active="${id}"] [data-fu-tools-menu="${id}"] {
          background-color: #1e293b;
          color: #22d3ee;
        }
        [data-fu-tools-menu-active]:not([data-fu-tools-menu-active="${id}"]) [data-fu-tools-content="${id}"] {
          display: none;
        }
      `
        )
        .join('')}

      [data-fu-tools-element-selector="active"] #app * {
        outline-offset: -2px !important;
        transition: 0.2s;
        outline-color: rgba(0, 255, 255, 0.5) !important;
      }
      [data-fu-tools-element-selector="active"] #app *:hover {
        cursor: pointer !important;
        outline-width: 2px !important;
        outline-style: solid !important;
        position: relative !important;
      }
      [data-fu-tools-element-selector="active"] #app *:not([data-fu-tools-element-name=""]):not(:has(*:hover)):hover::before {
        content: attr(data-fu-tools-element-name) !important;
        position: absolute !important;
        text-transform: none !important;
        left: 0 !important;
        top: 24px !important;
        background-color: rgba(0, 255, 255, 0.5) !important;
        color: black !important;
        padding: 2px 4px !important;
        font-size: 12px !important;
        font-weight: bold !important;
        border-radius: 4px !important;
        z-index: 9999 !important;
        white-space: nowrap !important;
        pointer-events: none !important;
        transform: translateY(-100%) !important;
        transform-origin: bottom left !important;
        transition: transform 0.2s ease-in-out !important;
        transition-delay: 0s !important;
      }
    </style>`
  )

  clearTablesListener()
  menuItemsListener()
  toggleSizeListener()
  toggleDevToolsListener()
  toggleElementSelectorListener()
  searchListener()
  serverCheckerListener()
  reactElementSelectorListener()

  Object.values(configuration.plugins).forEach(({ init }) => init?.())

  document.addEventListener('fu-tools-event', e => {
    const { pluginId, params } = (e as CustomEvent).detail
    if (!params || !pluginId) {
      return
    }

    const plugin = configuration.plugins.find(plugin => plugin.id === pluginId)
    if (!plugin) {
      console.warn(`[FuTools]: Unknown event type for '${pluginId}'`)
      return
    }

    plugin.render(...params)
  })
}

export { init }
